import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILink from 'UIComponents/link/UILink';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { useSelector } from 'react-redux';
import ApiNameToUpgradeProductMap from 'self-service-api/constants/ApiNameToUpgradeProductMap';
import { upgradeProductToTrialIds } from 'self-service-api/constants/Trials';
import { getAccountDashboardUrl } from 'self-service-api/core/utilities/links';
import styled from 'styled-components';
import { breakpointValues } from '../../constants/Breakpoints';
import { HighlightedProductsHeaderConfig } from '../../constants/merchCards/HighlightedProductsHeaderConfig';
import { MERCH_CARD } from '../../constants/tracking/Locations';
import { useAppSelector } from '../../reducers';
import { getIsCurrentTrialSelector } from '../../selectors/ActiveTrialsSelectors';
import { hasPricingAndPurchasingSucceededSelector, hasPurchaseMotionsFailedSelector, isMerchCardCTAsDataLoadingSelector } from '../../selectors/LoadingStateSelectors';
import { hasProductVersionInProductInventorySelector, ownedBundleProductContainingHubSelector } from '../../selectors/ProductInventorySelectors';
import { productSelector, productVersionSelector } from '../../selectors/ProductsSelectors';
import { usePricingLibSelector } from '../../selectors/SelectorUtils';
import { shouldShowTermSelectorForMerchSelector } from '../../selectors/TermSelectors';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import CurrentPlanTag from '../common/CurrentPlanTag';
import DetailedTermSelector from '../common/DetailedTermSelector';
import ProductIcon from '../common/ProductIcon';
import { SectionTitle } from '../common/SharedComponents';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import CompareCheckbox from '../compare/CompareCheckbox';
import AdditionalUnitsPicker from '../plan-builder/AdditionalUnitsPicker';
import RequiredOnboardingSubtextContainer from '../plan-builder/RequiredOnboardingSubtextContainer';
import FallbackMerchCardCTAsContainer from './FallbackMerchCardCTAsContainer';
import MerchCardCTAsContainer from './MerchCardCTAsContainer';
import { MerchCardWrapper, Section } from './MerchCardSharedComponents';
import BusinessObjectivesContainer from './business-objectives/BusinessObjectivesContainer';
import HighlightedMerchPriceDisplay from './price-display/HighlightedMerchPriceDisplay';
import { useSkuAdditionalUnits } from './useSkuAdditionalUnits';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledTermSelectorContainer = styled(DetailedTermSelector).withConfig({
  displayName: "HighlightedMerchCard__StyledTermSelectorContainer",
  componentId: "vf3nb2-0"
})(["max-width:300px;margin:0 auto;"]);
const StyledTitleContainer = styled(UIFlex).withConfig({
  displayName: "HighlightedMerchCard__StyledTitleContainer",
  componentId: "vf3nb2-1"
})(["@media (min-width:", "px) and (max-width:", "px){flex-direction:column;}"], breakpointValues.tablet, breakpointValues.sdMac - 1);
const HighlightedMerchCard = ({
  productLevel,
  productLine,
  commonStyles,
  selectedTab
}) => {
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const isMerchCardCTAsDataLoading = useSelector(isMerchCardCTAsDataLoadingSelector);
  const hasPurchaseMotionsFailed = useSelector(hasPurchaseMotionsFailedSelector);
  const product = useSelector(state => productSelector(state, {
    productLine,
    productLevel
  }));
  const apiName = product && product.apiName;
  const upgradeProduct = ApiNameToUpgradeProductMap[apiName];
  let trialId;
  if (upgradeProduct !== undefined) {
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    trialId = upgradeProductToTrialIds[upgradeProduct];
  }
  const isCurrentTrial = useSelector(state => {
    return getIsCurrentTrialSelector(state, trialId);
  });
  const seleniumId = apiName || 'loading';
  const {
    additionalUnitSkuId,
    isAdditionalUnitSkuIdAQuantityPack,
    additionalUnitQuantity,
    setAdditionalUnitQuantity,
    skuId
  } = useSkuAdditionalUnits({
    productLevel,
    productLine
  });
  const ownsProductVersion = useAppSelector(state => hasPricingAndPurchasingSucceeded && hasProductVersionInProductInventorySelector(state, {
    skuId
  }));
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const {
    title = '',
    subtitle = ''
  } = HighlightedProductsHeaderConfig[productLine][productLevel] || {};
  const shouldShowTermSelector = useSelector(state => skuId && shouldShowTermSelectorForMerchSelector(state, {
    skuId
  }));
  const additionalUnitsPickerProps = {
    additionalUnitSkuId,
    additionalUnitQuantity,
    setAdditionalUnitQuantity,
    productConfigurations: [createProductConfig({
      skuId: skuId || 0,
      quantity: 1
    })],
    compact: true,
    queryParamTerm,
    trackingSource: MERCH_CARD,
    productLevel
  };
  const ownedBundleProductContainingHub = useAppSelector(state => {
    if (!hasPricingAndPurchasingSucceeded) return;
    return ownedBundleProductContainingHubSelector(state, {
      skuId
    });
  });
  const bundleType = useAppSelector(state => {
    if (!ownedBundleProductContainingHub) return;
    return productVersionSelector(state, {
      skuId: ownedBundleProductContainingHub.skuId
    }).bundleType;
  });
  const ownsProductViaBundle = !!ownedBundleProductContainingHub;

  // In order for the grid to work, the wrapper element requires display: contents.
  // Children of the display grid item must get the grid-start attributes directly
  return /*#__PURE__*/_jsxs(MerchCardWrapper, {
    children: [/*#__PURE__*/_jsxs(Section, {
      className: "flex-column p-top-8",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 1,
        position: 'relative'
      }),
      "data-selenium-id": `${seleniumId}-merch-card`,
      children: [ownsProductVersion && /*#__PURE__*/_jsx(CurrentPlanTag, {
        message: "pricingPage.highlightedMerchCards.currentPlan"
      }), ownsProductViaBundle && /*#__PURE__*/_jsx(CurrentPlanTag, {
        message: `pricingPage.highlightedMerchCards.youOwnViaBundle.${bundleType}.${productLevel}`
      }), isCurrentTrial && /*#__PURE__*/_jsx(CurrentPlanTag, {
        message: "pricingPage.highlightedMerchCards.currentTrial"
      }), /*#__PURE__*/_jsxs(StyledTitleContainer, {
        align: "start",
        gap: 8,
        children: [/*#__PURE__*/_jsx(ProductIcon, {
          productLine: productLine,
          productLevel: productLevel,
          responsive: false,
          height: 24
        }), /*#__PURE__*/_jsx(SectionTitle, {
          className: "m-bottom-0",
          style: {
            maxWidth: '13ch'
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: title
          })
        })]
      })]
    }), /*#__PURE__*/_jsx(Section, {
      className: "flex-column p-top-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 2,
        position: 'relative'
      }),
      children: isSeatsRedesignUngated && ownsProductVersion ? /*#__PURE__*/_jsx(UILink, {
        href: getAccountDashboardUrl(),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.viewAccountButton.text"
        })
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: subtitle
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 3
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [20, 33, 20],
        widths: [60, 80, 140],
        className: "flex-column",
        Content: HighlightedMerchPriceDisplay,
        contentProps: {
          queryParamTerm,
          skuId,
          useLargePrice: true,
          quantityPackSkuId: isAdditionalUnitSkuIdAQuantityPack ? additionalUnitSkuId : null,
          quantityPackQuantity: isAdditionalUnitSkuIdAQuantityPack ? additionalUnitQuantity : 0
        }
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 4
      }),
      children: shouldShowTermSelector && /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [41],
        widths: [300],
        className: "flex-column align-center",
        Content: StyledTermSelectorContainer,
        contentProps: {
          skuIds: isAdditionalUnitSkuIdAQuantityPack && additionalUnitQuantity > 0 ? [skuId, additionalUnitSkuId] : [skuId],
          onChange: setQueryParamTerm,
          activeTerm: queryParamTerm,
          trackingSource: MERCH_CARD
        }
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 5
      }),
      children: (!isSeatsRedesignUngated || !ownsProductVersion) && /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        className: "flex-column align-start",
        heights: [34, 76],
        widths: ['75%', '100%'],
        Content: AdditionalUnitsPicker,
        contentProps: additionalUnitsPickerProps
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4 m-bottom-5",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 6
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isMerchCardCTAsDataLoading,
        isError: hasPurchaseMotionsFailed,
        heights: [47],
        Content: MerchCardCTAsContainer,
        contentProps: {
          queryParamTerm,
          skuId,
          productLine,
          additionalUnitSkuId,
          additionalUnitQuantity: isAdditionalUnitSkuIdAQuantityPack ? additionalUnitQuantity : 0,
          ctaStyles: {
            width: '100%',
            lineHeight: '23px'
          }
        },
        ErrorContent: FallbackMerchCardCTAsContainer,
        errorContentProps: {
          skuId
        }
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "flex-column align-center",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 7
      }),
      children: apiName && /*#__PURE__*/_jsx(CompareCheckbox, {
        apiName: apiName
      })
    }), /*#__PURE__*/_jsx(BusinessObjectivesContainer, {
      productLine: productLine,
      productLevel: productLevel,
      apiName: apiName,
      commonStyles: commonStyles
    }), /*#__PURE__*/_jsx(Section, {
      className: "flex-column align-center p-bottom-6 p-top-3",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 10
      }),
      children: skuId && /*#__PURE__*/_jsx(RequiredOnboardingSubtextContainer, {
        queryParamTerm: queryParamTerm,
        skuIds: [skuId],
        tab: selectedTab,
        trackingSource: MERCH_CARD,
        includesOnboardingCost: false
      })
    })]
  });
};
export default HighlightedMerchCard;